import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { concatMap, distinctUntilChanged, of, startWith, switchScan, timer } from 'rxjs'

import { latestAuthStateWithAccessToken$ } from '../auth/state'

export interface UtilityMenuState {
  isAnimated: boolean
  isAuthenticated: boolean
  isVisible: boolean
}

const initialState: UtilityMenuState = {
  isAnimated: true,
  isAuthenticated: false,
  isVisible: false,
}

export const [useUtilityMenuState, utilityMenuState$] = bind<UtilityMenuState>(
  // use `latestAuthStateWithAccessToken$` since it waits for authentication to settle before emitting
  latestAuthStateWithAccessToken$.pipe(
    switchScan(
      (prevState, { isAuthenticated }) =>
        // if already visible or not animated, just return the new state
        prevState.isVisible || !prevState.isAnimated
          ? of({
              isAnimated: false,
              isAuthenticated,
              isVisible: true,
            })
          : timer(1000).pipe(
              concatMap(() =>
                // disable animation after waiting for animation to complete (1s should be plenty)
                of({
                  isAnimated: false,
                  isAuthenticated,
                  isVisible: true,
                }),
              ),
              // emit initial state with animation enabled
              startWith({
                isAnimated: true,
                isAuthenticated,
                isVisible: true,
              }),
            ),
      initialState,
    ),
    distinctUntilChanged(
      (a, b) =>
        a.isVisible === b.isVisible &&
        a.isAnimated === b.isAnimated &&
        a.isAuthenticated === b.isAuthenticated,
    ),
  ),
  // specify initial value to avoid emitting Suspense
  initialState,
)

const [showSearchChange$, setShowSearch] = createSignal<boolean>()
const showSearch$ = showSearchChange$.pipe(startWith(false), distinctUntilChanged())

export { setShowSearch }
export const [useShowSearch] = bind(showSearch$)

const [activeItemChange$, setActiveItem] = createSignal<string | null | undefined>()
const activeItem$ = activeItemChange$.pipe(startWith(null), distinctUntilChanged())

export const [useActiveItem] = bind(activeItem$)
export { setActiveItem }

const [isMobileMenuOpenChange$, setIsMobileMenuOpen] = createSignal<boolean>()
const isMobileMenuOpen$ = isMobileMenuOpenChange$.pipe(startWith(false), distinctUntilChanged())

export const [useIsMobileMenuOpen] = bind(isMobileMenuOpen$)
export { setIsMobileMenuOpen }
